import '../../platform.css';
import './ownTeam.css';

import React from 'react';

import { useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { userConfigState } from '../../recoil/atoms/userConfigState';
import { userSettingsState } from '../../recoil/atoms/userSettingsState';
import { useSetSelectedPlayer } from '../../recoil/hooks/useSetSelectedPlayer';
import { competitionsState } from '../../recoil/atoms/competitionsState';
import { playerCareersState } from '../../recoil/atoms/playerCareersState';
import { playerOverviewsState } from '../../recoil/atoms/playerOverviewsState';
import { tenantScopesState } from '../../recoil/atoms/tenantScopesState';
import { tenantSettingsState } from '../../recoil/atoms/tenantSettingsState';
import { useAuthContext } from '../../../common/contexts/useAuthContext';
import { useAddPlayerCareers } from '../../recoil/hooks/useAddPlayerCareers';

import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ReplayIcon from '@mui/icons-material/Replay';

import { forceSimulation, forceX, forceY, forceCollide } from 'd3-force';

import { translate } from '../../../common/language/translations';
import { ViewContainerBackgroundSvg } from '../../../assets/svg/ViewContainerBackgroundSvg';
import { getDisplayEconomicValue, getLogairthmicValue } from '../../utils/economyUtils';
import { trackEvent } from '../../services/server/analytics/trackEvent';
import { PlayerImage } from '../../components/display/images/PlayerImage';
import { updateOwnTeamMatrixConfig } from '../../services/firestore/userSettings';
import { OwnTeamMatrixDimensionDropDown } from '../../components/controls/dropDowns/OwnTeamMatrixDimensionDropDown';
import { IconButton } from '../../components/controls/buttons/IconButton';
import { PlayerCareer, PlayerId, PlayerMap } from '../../types/player';
import { StringToAnyMap, StringToNumberMap } from '../../types/common';
import { Iteration } from '../../types/competition';
import { OwnTeamMatrixDimension } from '../../types/user';
import { getContractExpirationIndexEquivalent } from '../../utils/contractUtils';
import { getIndexColor } from '../../utils/colorUtils';
import { getDisplayPlayerName } from '../../utils/nameUtils';
import { playerRoleToIndexEquivalent } from '../../static/constants/playerRoles';
import { getLocalityIcon, getRoleIcon } from '../../utils/iconUtils';


interface AxisToPlayerToMinutes {
  [key: string]: {
    [key: PlayerId]: {
      minutesPlayed: number;
      percentageMinutesPlayed: number;
    }
  }
}

interface OwnTeamMatrixDataPoint {
  id: PlayerId;
  name: string;
  image_url: string;

  left: number | undefined; // percentage
  top: number | undefined; // percentage
  color: string;
}


interface OwnTeamMatrixViewProps {
  ownTeamPlayers: PlayerMap;
}

export const OwnTeamMatrixView: React.FC<OwnTeamMatrixViewProps> = ({ ownTeamPlayers }) => {

  const { currentUser } = useAuthContext();

  const userConfig = useRecoilValue(userConfigState);
  const userSettings = useRecoilValue(userSettingsState);
  const tenantSettings = useRecoilValue(tenantSettingsState);
  const tenantScopes = useRecoilValue(tenantScopesState);
  const competitions = useRecoilValue(competitionsState);

  const playerCareers = useRecoilValue(playerCareersState);
  const addPlayerCareers = useAddPlayerCareers();
  const playerOverviews = useRecoilValue(playerOverviewsState);
  const { setAndTrackSelectedPlayer } = useSetSelectedPlayer();

  const [data, setData] = useState<OwnTeamMatrixDataPoint[]>([]);
  const [simulationNodes, setSimulationNodes] = useState<OwnTeamMatrixDataPoint[]>([]);

  const [minAge, setMinAge] = useState<number | undefined>(undefined);
  const [maxAge, setMaxAge] = useState<number | undefined>(undefined);
  const [minSalaryPlusBonuses, setMinSalaryPlusBonuses] = useState<number | undefined>(undefined);
  const [maxSalaryPlusBonuses, setMaxSalaryPlusBonuses] = useState<number | undefined>(undefined);
  const [minSigningCost, setMinSigningCost] = useState<number | undefined>(undefined);
  const [maxSigningCost, setMaxSigningCost] = useState<number | undefined>(undefined);
  const [minMarketValue, setMinMarketValue] = useState<number | undefined>(undefined);
  const [maxMarketValue, setMaxMarketValue] = useState<number | undefined>(undefined);
  const [isStaticDimensionsSet, setIsStaticDimensionsSet] = useState(false);

  const [dropDownExpanded, setDropDownExpanded] = useState<'xAxis' | 'yAxis' | 'colorAxis' | undefined>(undefined);
  const [showPlayerNames, setShowPlayerNames] = useState(false);

  const [iterationDropDownOptions, setIterationDropDownOptions] = useState<Iteration[]>([]);
  const [axisToMaxMinutes, setAxisToMaxMinutes] = useState<StringToNumberMap>({});

  const [selectedXAxisIterations, setSelectedXAxisIterations] = useState<number[]>(userSettings?.ownTeamMatrixConfig?.xAxis?.iterations ?? []);
  const [selectedYAxisIterations, setSelectedYAxisIterations] = useState<number[]>(userSettings?.ownTeamMatrixConfig?.yAxis?.iterations ?? []);
  const [selectedColorAxisIterations, setSelectedColorAxisIterations] = useState<number[]>(userSettings?.ownTeamMatrixConfig?.colorAxis?.iterations ?? []);

  const defaultXAxisDimension = 'age';
  const defaultYAxisDimension = 'minutesPlayed';
  const defaultColorAxisDimension = userConfig?.isColorBlind ? null : 'contractLength';

  const [selectedXAxisDimension, setSelectedXAxisDimension] = useState<OwnTeamMatrixDimension | null>(() =>
    userSettings?.ownTeamMatrixConfig?.xAxis?.dimension ?? defaultXAxisDimension
  );
  const [selectedYAxisDimension, setSelectedYAxisDimension] = useState<OwnTeamMatrixDimension | null>(() =>
    userSettings?.ownTeamMatrixConfig?.yAxis?.dimension ?? defaultYAxisDimension
  );
  const [selectedColorAxisDimension, setSelectedColorAxisDimension] = useState<OwnTeamMatrixDimension | null>(() =>
    userSettings?.ownTeamMatrixConfig?.colorAxis?.dimension ?? defaultColorAxisDimension
  );


  const dimensionOptions = useMemo(() => {
    const options: OwnTeamMatrixDimension[] = ['age', 'contractLength', 'locality', 'skillIndex'];
    if (tenantScopes?.hasSkillcorner) options.push('physicalIndex');
    if (userConfig?.accessConfig.hasAccessToEconomy) options.push('marketValue', 'salaryPlusBonuses', 'signingCostLong');
    if (userConfig?.accessConfig.hasAccessToViewTeamRoles) options.push('teamRole');
    options.push('minutesPlayed');
    return options;
  },
  [
    userConfig,
    tenantScopes?.hasSkillcorner
  ]);


  const ageDividerValue = useMemo(() => {
    return userSettings?.ownTeamMatrixConfig?.ownTeamMatrixAgeDivider ?? 24;
  }, [userSettings?.ownTeamMatrixConfig?.ownTeamMatrixAgeDivider]);


  const handleArrowIconClick = async (decrement: boolean) => {
    if (!userConfig || !currentUser) return;

    updateOwnTeamMatrixConfig(
      {
        ownTeamMatrixAgeDivider: decrement ? ageDividerValue - 1 : ageDividerValue + 1
      },
      userConfig.email,
      userConfig.club,
      currentUser
    );
  };


  const handlePlayerClick = (player: OwnTeamMatrixDataPoint) => {
    setAndTrackSelectedPlayer({
      id: player.id,
      name: player.name,
      playerOverview: !isNaN(Number(player.id)) ? playerOverviews[Number(player.id)] : undefined
    });
  };


  const getDimensionIndexEquivalent = (
    player: StringToAnyMap,
    dimension: OwnTeamMatrixDimension | null,
    axis: 'xAxis' | 'yAxis' | 'colorAxis',
    currentAxisToMaxMinutes: StringToNumberMap,
    axisToPlayerToMinutes: AxisToPlayerToMinutes,
  ): number | undefined => {

    if (dimension === 'minutesPlayed') {
      return userSettings?.ownTeamMatrixConfig?.[axis]?.showPercentage
        ? axisToPlayerToMinutes[axis][player.id].percentageMinutesPlayed / 10
        : currentAxisToMaxMinutes[axis]
          ? axisToPlayerToMinutes[axis][player.id].minutesPlayed / currentAxisToMaxMinutes[axis] * 10
          : undefined;
    }

    if (dimension === 'age' && minAge && maxAge) {
      if (axis === 'colorAxis') {
        return player.age ? (maxAge - player.age) / (maxAge - minAge) * 10 : undefined;
      }
      return player.age ? (player.age - minAge) / (maxAge - minAge) * 10 : undefined;
    }

    if (dimension === 'contractLength') {
      return getContractExpirationIndexEquivalent(
        player.contract_expiration,
        axis === 'colorAxis' ? 6 : 0,
      );
    }

    if (dimension === 'skillIndex') {
      return player.skill_index;
    }

    if (dimension === 'physicalIndex') {
      return player.physical_index;
    }

    if (dimension === 'marketValue') {
      if (maxMarketValue !== undefined && minMarketValue !== undefined) {
        if (maxMarketValue === minMarketValue && player.market_value !== undefined) return 5;

        // if axis is color, we want to scale the market value logarithmically
        if (axis === 'colorAxis') {
          const logarithmicMarketValue = getLogairthmicValue(player.market_value);
          const logarithmicMaxMarketValue = getLogairthmicValue(maxMarketValue);

          return logarithmicMarketValue !== undefined && logarithmicMaxMarketValue !== undefined
            ? (logarithmicMarketValue - minMarketValue) / (logarithmicMaxMarketValue - minMarketValue) * 10
            : undefined;
        }

        return player.market_value
          ? (player.market_value - minMarketValue) / (maxMarketValue - minMarketValue) * 10
          : undefined;
      }
    }

    if (dimension === 'salaryPlusBonuses') {
      if (maxSalaryPlusBonuses !== undefined && minSalaryPlusBonuses !== undefined) {
        if (maxSalaryPlusBonuses === minSalaryPlusBonuses && player.salaryPlusBonuses !== undefined) return 5;

        if (axis === 'colorAxis') {
          return player.salaryPlusBonuses
            ? (maxSalaryPlusBonuses - player.salaryPlusBonuses) / (maxSalaryPlusBonuses - minSalaryPlusBonuses) * 10
            : undefined;
        }

        return player.salaryPlusBonuses
          ? (player.salaryPlusBonuses - minSalaryPlusBonuses) / (maxSalaryPlusBonuses - minSalaryPlusBonuses) * 10
          : undefined;
      }
    }

    if (dimension === 'signingCostLong') {
      if (maxSigningCost !== undefined && minSigningCost !== undefined) {
        if (maxSigningCost === minSigningCost && player.signingCost !== undefined) return 5;

        if (axis === 'colorAxis') {
          return player.signingCost
            ? (maxSigningCost - player.signingCost) / (maxSigningCost - minSigningCost) * 10
            : undefined;
        }

        return player.signingCost
          ? (player.signingCost - minSigningCost) / (maxSigningCost - minSigningCost) * 10
          : undefined;
      }
    }

    if (dimension === 'locality') {
      return player.locality === 'local'
        ? 10
        : player.locality === 'national'
          ? 5
          : player.locality === 'international'
            ? 0
            : undefined;
    }

    if (dimension === 'teamRole') {
      return player.role ? playerRoleToIndexEquivalent[player.role] : undefined;
    }
  };


  const handleSelectDimension = (
    value: OwnTeamMatrixDimension,
    isOptionAlreadySelected: boolean,
    axis: 'xAxis' | 'yAxis' | 'colorAxis',
  ) => {
    if (!userConfig || !currentUser) return;

    const newDimension = isOptionAlreadySelected ? null : value;
    const setDimension = axis === 'xAxis' ? setSelectedXAxisDimension : axis === 'yAxis' ? setSelectedYAxisDimension : setSelectedColorAxisDimension;
    setDimension(newDimension);

    // reset iterations and showPercentage whenever a new dimension is selected
    const setIterations = axis === 'xAxis' ? setSelectedXAxisIterations : axis === 'yAxis' ? setSelectedYAxisIterations : setSelectedColorAxisIterations;
    setIterations([]);

    if (!isOptionAlreadySelected) {
      setDropDownExpanded(undefined);
    }

    updateOwnTeamMatrixConfig(
      {
        [axis]: {
          dimension: newDimension,
          iterations: [],
          showPercentage: false,
        }
      },
      userConfig.email,
      userConfig.club,
      currentUser
    );
  };


  const handleSelectIteration = (
    value: number,
    isOptionAlreadySelected: boolean,
    axis: 'xAxis' | 'yAxis' | 'colorAxis'
  ) => {
    if (!userConfig || !currentUser) return;

    const currentIterations = axis === 'xAxis' ? selectedXAxisIterations : axis === 'yAxis' ? selectedYAxisIterations : selectedColorAxisIterations;
    const newIterations = isOptionAlreadySelected ? currentIterations.filter(iteration => iteration !== value) : [...currentIterations, value];
    const setIterations = axis === 'xAxis' ? setSelectedXAxisIterations : axis === 'yAxis' ? setSelectedYAxisIterations : setSelectedColorAxisIterations;
    setIterations(newIterations);

    // set dimension to minutesPlayed if there are iterations, otherwise set to null, and reset showPercentage if there are no iterations
    const newDimension = newIterations.length > 0 ? 'minutesPlayed' : null;
    const setDimension = axis === 'xAxis' ? setSelectedXAxisDimension : axis === 'yAxis' ? setSelectedYAxisDimension : setSelectedColorAxisDimension;
    setDimension(newDimension);

    const newShowPercentage = newIterations.length === 0 ? false : userSettings?.ownTeamMatrixConfig?.[axis]?.showPercentage ?? false;

    updateOwnTeamMatrixConfig(
      {
        [axis]: { dimension: newDimension, iterations: newIterations, showPercentage: newShowPercentage }
      },
      userConfig.email,
      userConfig.club,
      currentUser
    );
  };


  const handleTogglePercentage = (
    value: boolean,
    axis: 'xAxis' | 'yAxis' | 'colorAxis'
  ) => {
    if (!userConfig || !currentUser) return;

    // if toggled on, dimension should always be set to minutesPlayed, and we select one iteration if none are selected
    // if toggled off, the current dimension is minutesPlayed, and at least one iteration is selected, so we do nothing

    let newIterations = axis === 'xAxis' ? selectedXAxisIterations : axis === 'yAxis' ? selectedYAxisIterations : selectedColorAxisIterations;
    if (value && newIterations.length === 0 && iterationDropDownOptions.length > 0) {
      newIterations = [iterationDropDownOptions[0].iterationId];
    }
    const setIterations = axis === 'xAxis' ? setSelectedXAxisIterations : axis === 'yAxis' ? setSelectedYAxisIterations : setSelectedColorAxisIterations;
    setIterations(newIterations);

    const newDimension = 'minutesPlayed';
    const setDimension = axis === 'xAxis' ? setSelectedXAxisDimension : axis === 'yAxis' ? setSelectedYAxisDimension : setSelectedColorAxisDimension;
    setDimension(newDimension);

    updateOwnTeamMatrixConfig(
      {
        [axis]: { dimension: newDimension, iterations: newIterations, showPercentage: value }
      },
      userConfig.email,
      userConfig.club,
      currentUser
    );
  };


  const handleResetDimensions = () => {
    if (!userConfig || !currentUser) return;

    setSelectedXAxisDimension(defaultXAxisDimension);
    setSelectedYAxisDimension(defaultYAxisDimension);
    setSelectedColorAxisDimension(defaultColorAxisDimension);

    setSelectedXAxisIterations([]);
    setSelectedYAxisIterations(iterationDropDownOptions.length > 0 ? [iterationDropDownOptions[0].iterationId] : []);
    setSelectedColorAxisIterations([]);

    updateOwnTeamMatrixConfig(
      {
        xAxis: { dimension: null, iterations: [], showPercentage: false },
        yAxis: { dimension: null, iterations: [], showPercentage: false },
        colorAxis: { dimension: null, iterations: [], showPercentage: false },
      },
      userConfig.email,
      userConfig.club,
      currentUser
    );
  };


  useEffect(() => {
    if (tenantSettings && competitions) {
      const iterations = tenantSettings.iterations;

      let currentIteration: number | undefined = undefined;
      iterations.forEach((iteration: Iteration) => {
        const competitionId = Number(iteration.competitionId);
        if (!currentIteration && competitions[competitionId] && competitions[competitionId].type === 'League') {
          currentIteration = iteration.iterationId;
        }
      });

      if (currentIteration) {
        if (selectedXAxisDimension === 'minutesPlayed' && selectedXAxisIterations.length === 0) {
          setSelectedXAxisIterations([currentIteration]);
        }
        if (selectedYAxisDimension === 'minutesPlayed' && selectedYAxisIterations.length === 0) {
          setSelectedYAxisIterations([currentIteration]);
        }
        if (selectedColorAxisDimension === 'minutesPlayed' && selectedColorAxisIterations.length === 0) {
          setSelectedColorAxisIterations([currentIteration]);
        }
      }

      setIterationDropDownOptions(iterations);
    }
  }, [tenantSettings, competitions]); // eslint-disable-line react-hooks/exhaustive-deps


  // set min and max values for static dimensions
  useEffect(() => {
    if (ownTeamPlayers && ageDividerValue) {

      let currentMinAge: number | undefined = undefined;
      let currentMaxAge: number | undefined = undefined;

      let currentMinSalaryPlusBonuses: number | undefined = undefined;
      let currentMaxSalaryPlusBonuses: number | undefined = undefined;

      let currentMinSigningCost: number | undefined = undefined;
      let currentMaxSigningCost: number | undefined = undefined;

      let currentMinMarketValue: number | undefined = undefined;
      let currentMaxMarketValue: number | undefined = undefined;

      Object.keys(ownTeamPlayers).forEach(key => {
        const player = ownTeamPlayers[key];

        if (player.age) {
          if (currentMinAge === undefined || player.age < currentMinAge) currentMinAge = player.age;
          if (currentMaxAge === undefined || player.age > currentMaxAge) currentMaxAge = player.age;
        }

        if (player.salaryPlusBonuses) {
          if (currentMinSalaryPlusBonuses === undefined || player.salaryPlusBonuses < currentMinSalaryPlusBonuses) {
            currentMinSalaryPlusBonuses = player.salaryPlusBonuses;
          }
          if (currentMaxSalaryPlusBonuses === undefined || player.salaryPlusBonuses > currentMaxSalaryPlusBonuses) {
            currentMaxSalaryPlusBonuses = player.salaryPlusBonuses;
          }
        }

        if (player.signingCost) {
          if (currentMinSigningCost === undefined || player.signingCost < currentMinSigningCost) currentMinSigningCost = player.signingCost;
          if (currentMaxSigningCost === undefined || player.signingCost > currentMaxSigningCost) currentMaxSigningCost = player.signingCost;
        }

        if (player.market_value) {
          if (currentMinMarketValue === undefined || player.market_value < currentMinMarketValue) currentMinMarketValue = player.market_value;
          if (currentMaxMarketValue === undefined || player.market_value > currentMaxMarketValue) currentMaxMarketValue = player.market_value;
        }
      });

      let currentMinAgeFloor = currentMinAge ? Math.floor(currentMinAge) : currentMinAge;
      let currentMaxAgeCeil = currentMaxAge ? Math.ceil(currentMaxAge) : currentMaxAge;

      if (currentMinAge && currentMinAgeFloor && currentMaxAge && currentMaxAgeCeil) {
        if (currentMinAge - currentMinAgeFloor < 0.5) currentMinAgeFloor--;
        if (currentMaxAgeCeil - currentMaxAge < 0.5) currentMaxAgeCeil++;

        currentMinAgeFloor = Math.min(currentMinAgeFloor, ageDividerValue - 1);
        currentMaxAgeCeil = Math.max(currentMaxAgeCeil, ageDividerValue + 1);
      }

      setMinAge(currentMinAgeFloor);
      setMaxAge(currentMaxAgeCeil);

      setMaxSalaryPlusBonuses(currentMaxSalaryPlusBonuses);
      setMinSalaryPlusBonuses(currentMinSalaryPlusBonuses);
      setMaxSigningCost(currentMaxSigningCost);
      setMinSigningCost(currentMinSigningCost);
      setMaxMarketValue(currentMaxMarketValue);
      setMinMarketValue(currentMinMarketValue);

      setIsStaticDimensionsSet(true);
    }
  }, [ownTeamPlayers, ageDividerValue]);


  // fetch player careers if not already fetched
  useEffect(() => {
    if (ownTeamPlayers && playerOverviews) {
      const playerIdsToFetch = Object.keys(ownTeamPlayers).filter(playerId => {
        const numberId = Number(playerId);
        return !isNaN(numberId) && !playerCareers[numberId] && playerOverviews[numberId]?.basic_stats_exist;
      });
      if (playerIdsToFetch.length > 0) {
        addPlayerCareers(playerIdsToFetch, currentUser);

        // ownTeamPlayers should always have careers loaded
        trackEvent(
          'UnexpectedBehavior',
          {
            file: 'OwnTeamMatrixView.tsx',
            behavior: 'ClubIterationMap not found in matrix for ' + playerIdsToFetch.length + ' players: ' + playerIdsToFetch.join(', ')
          },
          currentUser,
          'system'
        );
      }
    }
  }, [ownTeamPlayers, playerOverviews]); // eslint-disable-line react-hooks/exhaustive-deps


  // set matrix data
  useEffect(() => {
    if (ownTeamPlayers && playerCareers && playerOverviews && isStaticDimensionsSet && iterationDropDownOptions.length > 0 && tenantSettings) {

      const allPlayerCareersAreLoaded = Object.keys(ownTeamPlayers).every(playerId => {
        const numberId = Number(playerId);
        const shouldHaveCareer = !isNaN(numberId) && playerOverviews[Number(playerId)]?.basic_stats_exist;
        return !shouldHaveCareer || playerCareers[numberId];
      });

      if (allPlayerCareersAreLoaded) {

        // axis to minutes info for that axis based on the selected iterations
        const currentAxisToMaxMinutes: StringToNumberMap = {};
        const axisToPlayerToMinutes: AxisToPlayerToMinutes = {};

        ['xAxis', 'yAxis', 'colorAxis'].forEach(axis => {

          const selectedDimension = axis === 'xAxis' ? selectedXAxisDimension : axis === 'yAxis' ? selectedYAxisDimension : selectedColorAxisDimension;
          const selectedIterations = axis === 'xAxis' ? selectedXAxisIterations : axis === 'yAxis' ? selectedYAxisIterations : selectedColorAxisIterations;

          if (selectedDimension === 'minutesPlayed') {
            currentAxisToMaxMinutes[axis] = 0;
            axisToPlayerToMinutes[axis] = {};

            for (const playerId of Object.keys(ownTeamPlayers)) {

              let minutesPlayed = 0;
              let possibleMinutes = 0;

              const numberId = Number(playerId);

              if (!isNaN(numberId)) {
                const playerCareer: PlayerCareer | undefined = playerCareers[numberId];

                if (playerCareer) {
                  selectedIterations.forEach(iteration => {
                    const clubIterationKey = `${tenantSettings.clubId},${iteration}`;
                    const clubIteration = playerCareer[clubIterationKey];

                    if (clubIteration && clubIteration.basic_stats) {
                      minutesPlayed += clubIteration.basic_stats.minutes_played;
                      possibleMinutes += clubIteration.basic_stats.total_possible_minutes;
                    }
                  });

                  if (minutesPlayed > currentAxisToMaxMinutes[axis]) currentAxisToMaxMinutes[axis] = minutesPlayed;
                }
              }

              axisToPlayerToMinutes[axis][playerId] = {
                minutesPlayed: minutesPlayed,
                percentageMinutesPlayed: possibleMinutes ? (minutesPlayed / possibleMinutes) * 100 : 0
              };
            }

            currentAxisToMaxMinutes[axis] = Math.ceil(currentAxisToMaxMinutes[axis] / 100) * 100;
          }
        });

        setAxisToMaxMinutes(currentAxisToMaxMinutes);

        const matrixData: OwnTeamMatrixDataPoint[] = Object.keys(ownTeamPlayers).map(playerId => {
          const player = ownTeamPlayers[playerId];

          const xAxisIndexEquivalent = getDimensionIndexEquivalent(
            player, selectedXAxisDimension, 'xAxis', currentAxisToMaxMinutes, axisToPlayerToMinutes
          );

          const yAxisIndexEquivalent = getDimensionIndexEquivalent(
            player, selectedYAxisDimension, 'yAxis', currentAxisToMaxMinutes, axisToPlayerToMinutes
          );

          const colorAxisIndexEquivalent = getDimensionIndexEquivalent(
            player, selectedColorAxisDimension, 'colorAxis', currentAxisToMaxMinutes, axisToPlayerToMinutes
          );

          const left = Math.min(Math.max(xAxisIndexEquivalent !== undefined ? xAxisIndexEquivalent * 10 : 0, 1), 99);
          const top = Math.min(Math.max(yAxisIndexEquivalent !== undefined ? (100 - (yAxisIndexEquivalent * 10)) : 100, 1), 99);

          const matrixDataPoint: OwnTeamMatrixDataPoint = {
            id: player.id,
            name: player.name,
            image_url: player.image_url ?? 'https://media.api-sports.io/football/players/0.png',

            left: left,
            top: top,
            color: colorAxisIndexEquivalent !== undefined ? getIndexColor(colorAxisIndexEquivalent) : '#ffffff33',
          };

          return matrixDataPoint;
        });

        setData(matrixData);
      }
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [
    ownTeamPlayers,
    playerCareers,
    playerOverviews,
    selectedXAxisDimension,
    selectedYAxisDimension,
    selectedColorAxisDimension,
    selectedXAxisIterations,
    selectedYAxisIterations,
    selectedColorAxisIterations,
    userSettings?.ownTeamMatrixConfig,
    isStaticDimensionsSet,
    minAge,
    maxAge,
    iterationDropDownOptions.length,
    tenantSettings,
  ]);


  useEffect(() => {

    const nodes = data.map(player => ({
      ...player,
      x: player.left || 0,
      y: player.top || 0,
    }));

    const simulation = forceSimulation(nodes)
      .force('x', forceX(d => d.x as number).strength(0.1))
      .force('y', forceY(d => d.y as number).strength(0.1))
      .force('collide', forceCollide(1.5))
      .stop();

    for (let i = 0; i < 100; i++) simulation.tick();

    setSimulationNodes(nodes.map(d => ({ ...d, left: d.x, top: d.y })));
  }, [data]);


  useEffect(() => {
    if (data.length > 0) {
      const timer = setTimeout(() => {
        setShowPlayerNames(true);
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [data]);


  const getPlayerNameContainerStyle = (index: number, diameter: number) => {
    const element = document.getElementById('own-team-matrix-view-player-name-container-' + index);
    return {
      marginLeft: element ? ((diameter / 2) - element.clientWidth / 2) : 0,
    };
  };


  const getAxisLabels = (dimension: OwnTeamMatrixDimension | null, axis: 'xAxis' | 'yAxis' | 'colorAxis'): {
    label?: string,
    iconLabel?: React.JSX.Element,
    indexEquivalent: number,
    showLine?: boolean,
    isDynamicLabel?: boolean,
  }[] => {

    if (dimension === 'minutesPlayed') {
      return [
        {
          label: userSettings?.ownTeamMatrixConfig?.[axis]?.showPercentage ? '0%' : '0 min',
          indexEquivalent: 0,
        },
        {
          label: userSettings?.ownTeamMatrixConfig?.[axis]?.showPercentage ? '50%' : axisToMaxMinutes[axis] / 2 + ' min',
          indexEquivalent: 5,
          showLine: true,
        },
        {
          label: userSettings?.ownTeamMatrixConfig?.[axis]?.showPercentage ? '100%' : axisToMaxMinutes[axis] + ' min',
          indexEquivalent: 10,
        },
      ];
    }

    if (dimension === 'age') {
      const minLabel = minAge !== undefined ? minAge + ' ' + translate('years', userConfig?.language, true) : '';
      const maxLabel = maxAge !== undefined ? maxAge + ' ' + translate('years', userConfig?.language, true) : '';

      const labels: { label: string, indexEquivalent: number, showLine?: boolean, isDynamicLabel?: boolean }[] = [
        {
          label: axis === 'colorAxis' ? maxLabel : minLabel,
          indexEquivalent: 0,
        },
        {
          label: axis === 'colorAxis' ? minLabel : maxLabel,
          indexEquivalent: 10,
        },
      ];

      if (minAge !== undefined && maxAge !== undefined) {
        labels.push({
          label: ageDividerValue !== undefined ? ageDividerValue + ' ' + translate('years', userConfig?.language, true) : '',
          indexEquivalent: (ageDividerValue - minAge) / (maxAge - minAge) * 10,
          showLine: true,
          isDynamicLabel: true,
        });
      }

      return labels;
    }

    if (dimension === 'contractLength') {
      return [
        {
          label: axis === 'colorAxis' ? '< 6 ' + translate('monthsShort', userConfig?.language) : '0 ' + translate('years', userConfig?.language),
          indexEquivalent: 0,
        },
        {
          label: '2 ' + translate('years', userConfig?.language),
          indexEquivalent: 5,
          showLine: true,
        },
        {
          label: '4+ ' + translate('years', userConfig?.language, true),
          indexEquivalent: 10,
        },
      ];
    }

    if (dimension === 'skillIndex' || dimension === 'physicalIndex') {
      return [
        { label: '0', indexEquivalent: 0 },
        { label: '5', indexEquivalent: 5, showLine: true },
        { label: '10', indexEquivalent: 10 },
      ];
    }

    if (dimension === 'salaryPlusBonuses') {
      const minLabel = minSalaryPlusBonuses !== undefined ? getDisplayEconomicValue(minSalaryPlusBonuses, true) : '';
      const maxLabel = maxSalaryPlusBonuses !== undefined ? getDisplayEconomicValue(maxSalaryPlusBonuses, true) : '';

      return [
        {
          label: axis === 'colorAxis' ? maxLabel : minLabel,
          indexEquivalent: 0,
        },
        {
          label: maxSalaryPlusBonuses !== undefined && minSalaryPlusBonuses !== undefined
            ? getDisplayEconomicValue((maxSalaryPlusBonuses + minSalaryPlusBonuses) / 2, true)
            : '',
          indexEquivalent: 5,
          showLine: true,
        },
        {
          label: axis === 'colorAxis' ? minLabel : maxLabel,
          indexEquivalent: 10,
        },
      ];
    }

    if (dimension === 'signingCostLong') {
      const minLabel = minSigningCost !== undefined ? getDisplayEconomicValue(minSigningCost, true) : '';
      const maxLabel = maxSigningCost !== undefined ? getDisplayEconomicValue(maxSigningCost, true) : '';

      return [
        {
          label: axis === 'colorAxis' ? maxLabel : minLabel,
          indexEquivalent: 0,
        },
        {
          label: maxSigningCost !== undefined && minSigningCost !== undefined
            ? getDisplayEconomicValue((maxSigningCost + minSigningCost) / 2, true)
            : '',
          indexEquivalent: 5,
          showLine: true,
        },
        {
          label: axis === 'colorAxis' ? minLabel : maxLabel,
          indexEquivalent: 10,
        },
      ];
    }

    if (dimension === 'marketValue') {
      return [
        {
          label: minMarketValue !== undefined ? getDisplayEconomicValue(minMarketValue, true) : '',
          indexEquivalent: 0,
        },
        {
          label: maxMarketValue !== undefined && minMarketValue !== undefined
            ? getDisplayEconomicValue((maxMarketValue + minMarketValue) / 2, true)
            : '',
          indexEquivalent: 5,
          showLine: true,
        },
        {
          label: maxMarketValue !== undefined ? getDisplayEconomicValue(maxMarketValue, true) : '',
          indexEquivalent: 10,
        },
      ];
    }

    if (dimension === 'locality') {
      return [
        {
          iconLabel: getLocalityIcon('international', 20, '#ffffffaa'),
          indexEquivalent: 0,
        },
        {
          iconLabel: getLocalityIcon('national', 20, '#ffffffaa'),
          indexEquivalent: 5,
          showLine: true,
        },
        {
          iconLabel: getLocalityIcon('local', 20, '#ffffffaa'),
          indexEquivalent: 10,
        },
      ];
    }

    if (dimension === 'teamRole') {
      return [
        {
          iconLabel: getRoleIcon('loanedOut', 20, '#ffffffaa'),
          indexEquivalent: playerRoleToIndexEquivalent['loanedOut'],
          showLine: true,
        },
        {
          iconLabel: getRoleIcon('longTermInjured', 20, '#ffffffaa'),
          indexEquivalent: playerRoleToIndexEquivalent['longTermInjured'],
          showLine: true,
        },
        {
          iconLabel: getRoleIcon('reserve', 20, '#ffffffaa'),
          indexEquivalent: playerRoleToIndexEquivalent['reserve'],
          showLine: true,
        },
        {
          iconLabel: getRoleIcon('developmentPlayer', 20, '#ffffffaa'),
          indexEquivalent: playerRoleToIndexEquivalent['developmentPlayer'],
          showLine: true,
        },
        {
          iconLabel: getRoleIcon('loanedIn', 20, '#ffffffaa'),
          indexEquivalent: playerRoleToIndexEquivalent['loanedIn'],
          showLine: true,
        },
        {
          iconLabel: getRoleIcon('rotationPlayer', 20, '#ffffffaa'),
          indexEquivalent: playerRoleToIndexEquivalent['rotationPlayer'],
          showLine: true,
        },
        {
          iconLabel: getRoleIcon('valuePlayer', 20, '#ffffffaa'),
          indexEquivalent: playerRoleToIndexEquivalent['valuePlayer'],
          showLine: true,
        },
        {
          iconLabel: getRoleIcon('keyPlayer', 20, '#ffffffaa'),
          indexEquivalent: playerRoleToIndexEquivalent['keyPlayer'],
          showLine: true,
        },
      ];
    }

    return [];
  };

  const getVerticalLinesAndLabels = () => {
    if (!selectedXAxisDimension) return null;

    const labels = getAxisLabels(selectedXAxisDimension, 'xAxis');

    return (
      <React.Fragment>
        {labels.map((label, index) => {

          const isMinLine = label.indexEquivalent === 0 && !label.showLine;
          const isMaxLine = label.indexEquivalent === 10 && !label.showLine;

          const showLeftArrow = selectedXAxisDimension === 'age' && label.isDynamicLabel && minAge !== undefined && ageDividerValue > minAge + 2;
          const showRightArrow = selectedXAxisDimension === 'age' && label.isDynamicLabel && maxAge !== undefined && ageDividerValue < maxAge - 2;

          let className = 'own-team-matrix-view-vertical-line';
          if (isMinLine) className += ' own-team-matrix-view-vertical-label-line-min';
          if (isMaxLine) className += ' own-team-matrix-view-vertical-label-line-max';

          return (
            <div key={index + selectedXAxisDimension}>

              <div
                className={className}
                style={{ left: (label.indexEquivalent * 10) + '%', transition: label.isDynamicLabel ? 'left 250ms' : undefined }}
              />

              <div
                className='own-team-matrix-view-label own-team-matrix-view-vertical-label'
                style={{ left: (label.indexEquivalent * 10) + '%', transition: label.isDynamicLabel ? 'left 250ms' : undefined }}
              >
                {showLeftArrow && (
                  <div
                    className='own-team-matrix-view-arrow-icon'
                    onClick={() => handleArrowIconClick(true)}
                  >
                    <KeyboardArrowLeftIcon style={{ fontSize: 18 }} />
                  </div>
                )}
                {!showLeftArrow && <div style={{ width: 20 }}>&nbsp;</div>}

                {label.label ?? label.iconLabel}

                {showRightArrow && (
                  <div
                    className='own-team-matrix-view-arrow-icon'
                    onClick={() => handleArrowIconClick(false)}
                  >
                    <KeyboardArrowRightIcon style={{ fontSize: 18 }} />
                  </div>
                )}
                {!showRightArrow && <div style={{ width: 20 }}>&nbsp;</div>}
              </div>
            </div>
          );
        })}
      </React.Fragment>
    );
  };

  const getHorizontalLinesAndLabels = () => {
    if (!selectedYAxisDimension) return null;

    const labels = getAxisLabels(selectedYAxisDimension, 'yAxis');

    return (
      <React.Fragment>
        {labels.map((label, index) => {

          const isMinLine = label.indexEquivalent === 0 && !label.showLine;
          const isMaxLine = label.indexEquivalent === 10 && !label.showLine;

          const showBottomArrow = selectedXAxisDimension === 'age' && label.isDynamicLabel && minAge !== undefined && ageDividerValue > minAge + 2;
          const showTopArrow = selectedXAxisDimension === 'age' && label.isDynamicLabel && maxAge !== undefined && ageDividerValue < maxAge - 2;

          let className = 'own-team-matrix-view-horizontal-line';
          if (isMinLine) className += ' own-team-matrix-view-horizontal-label-line-min';
          if (isMaxLine) className += ' own-team-matrix-view-horizontal-label-line-max';

          const fontSize = label.label
            ? label.label.length > 16 ? '8px' : label.label.length > 14 ? '9px' : label.label.length > 12 ? '10px' : undefined
            : undefined;

          return (
            <div key={index + selectedYAxisDimension}>
              <div
                className={className}
                style={{ top: (100 - (label.indexEquivalent * 10)) + '%', transition: label.isDynamicLabel ? 'top 250ms' : undefined }}
              />

              <div
                className='own-team-matrix-view-label own-team-matrix-view-horizontal-label'
                style={{
                  top: (100 - (label.indexEquivalent * 10)) + '%',
                  transition: label.isDynamicLabel ? 'top 250ms' : undefined,
                  fontSize: fontSize,
                }}
              >
                {showTopArrow && (
                  <div
                    className='own-team-matrix-view-arrow-icon'
                    onClick={() => handleArrowIconClick(false)}
                  >
                    <KeyboardArrowUpIcon style={{ fontSize: 18 }} />
                  </div>
                )}
                {!showTopArrow && <div style={{ height: 20 }}>&nbsp;</div>}

                {label.label ?? label.iconLabel}

                {showBottomArrow && (
                  <div
                    className='own-team-matrix-view-arrow-icon'
                    onClick={() => handleArrowIconClick(true)}
                  >
                    <KeyboardArrowDownIcon style={{ fontSize: 18 }} />
                  </div>
                )}
                {!showBottomArrow && <div style={{ height: 20 }}>&nbsp;</div>}
              </div>
            </div>
          );
        })}
      </React.Fragment>
    );
  };


  const colorAxisLabels = getAxisLabels(selectedColorAxisDimension, 'colorAxis');

  return (
    <div className='full-size-container'>

      <div className='full-size'>
        <ViewContainerBackgroundSvg />
      </div>

      {dropDownExpanded && <div className='filter-section-empty-background' onClick={() => setDropDownExpanded(undefined)} />}

      <IconButton
        icon={<ReplayIcon style={{ fontSize: 18 }} />}
        onClick={handleResetDimensions}
        size={20}
        style={{ position: 'absolute', left: 'calc(2% - 9px)', top: 48 }}
        title={translate('resetAxes', userConfig?.language)}
      />

      <div className='own-team-matrix-view-filter-section'>

        <div className='own-team-matrix-view-drop-down-title own-team-matrix-view-drop-down-yAxis'>
          {translate('yAxis', userConfig?.language)}
        </div>
        <div className='own-team-matrix-view-drop-down own-team-matrix-view-drop-down-yAxis'>
          <OwnTeamMatrixDimensionDropDown
            id={'own-team-matrix-view-drop-down-yAxis'}
            dropDownOptions={dimensionOptions}
            selectedOptions={selectedYAxisDimension ? [selectedYAxisDimension] : []}
            handleOptionSelect={
              (value: OwnTeamMatrixDimension, isOptionAlreadySelected: boolean) => handleSelectDimension(value, isOptionAlreadySelected, 'yAxis')
            }
            iterationOptions={iterationDropDownOptions}
            selectedIterations={selectedYAxisIterations}
            handleIterationSelect={(value: number, isOptionAlreadySelected: boolean) => handleSelectIteration(value, isOptionAlreadySelected, 'yAxis')}
            isPercentageToggled={userSettings?.ownTeamMatrixConfig?.yAxis?.showPercentage ?? false}
            handleTogglePercentage={(value: boolean) => handleTogglePercentage(value, 'yAxis')}
            isDropDownExpanded={dropDownExpanded === 'yAxis'}
            setIsDropDownExpanded={(value: boolean) => setDropDownExpanded(value ? 'yAxis' : undefined)}
            defaultDropDownText={'select'}
            defaultDropDownTextColor={'#00000080'}
            marginBetweenOptions={3}
            maxHeight={'80vh'}
          />
        </div>

        <div className='own-team-matrix-view-drop-down-title own-team-matrix-view-drop-down-xAxis'>
          {translate('xAxis', userConfig?.language)}
        </div>
        <div className='own-team-matrix-view-drop-down own-team-matrix-view-drop-down-xAxis'>
          <OwnTeamMatrixDimensionDropDown
            id={'own-team-matrix-view-drop-down-xAxis'}
            dropDownOptions={dimensionOptions}
            selectedOptions={selectedXAxisDimension ? [selectedXAxisDimension] : []}
            handleOptionSelect={
              (value: OwnTeamMatrixDimension, isOptionAlreadySelected: boolean) => handleSelectDimension(value, isOptionAlreadySelected, 'xAxis')
            }
            iterationOptions={iterationDropDownOptions}
            selectedIterations={selectedXAxisIterations}
            handleIterationSelect={(value: number, isOptionAlreadySelected: boolean) => handleSelectIteration(value, isOptionAlreadySelected, 'xAxis')}
            isPercentageToggled={userSettings?.ownTeamMatrixConfig?.xAxis?.showPercentage ?? false}
            handleTogglePercentage={(value: boolean) => handleTogglePercentage(value, 'xAxis')}
            isDropDownExpanded={dropDownExpanded === 'xAxis'}
            setIsDropDownExpanded={(value: boolean) => setDropDownExpanded(value ? 'xAxis' : undefined)}
            defaultDropDownText={'select'}
            defaultDropDownTextColor={'#00000080'}
            marginBetweenOptions={3}
            maxHeight={'80vh'}
          />
        </div>

        <div className='own-team-matrix-view-drop-down-title own-team-matrix-view-drop-down-colorAxis'>
          {translate('colorBasis', userConfig?.language)}
        </div>
        <div className='own-team-matrix-view-drop-down own-team-matrix-view-drop-down-colorAxis'>
          <OwnTeamMatrixDimensionDropDown
            id={'own-team-matrix-view-drop-down-colorAxis'}
            dropDownOptions={dimensionOptions}
            selectedOptions={selectedColorAxisDimension ? [selectedColorAxisDimension] : []}
            handleOptionSelect={
              (value: OwnTeamMatrixDimension, isOptionAlreadySelected: boolean) => handleSelectDimension(value, isOptionAlreadySelected, 'colorAxis')
            }
            iterationOptions={iterationDropDownOptions}
            selectedIterations={selectedColorAxisIterations}
            handleIterationSelect={(value: number, isOptionAlreadySelected: boolean) => handleSelectIteration(value, isOptionAlreadySelected, 'colorAxis')}
            isPercentageToggled={userSettings?.ownTeamMatrixConfig?.colorAxis?.showPercentage ?? false}
            handleTogglePercentage={(value: boolean) => handleTogglePercentage(value, 'colorAxis')}
            isDropDownExpanded={dropDownExpanded === 'colorAxis'}
            setIsDropDownExpanded={(value: boolean) => setDropDownExpanded(value ? 'colorAxis' : undefined)}
            defaultDropDownText={'select'}
            defaultDropDownTextColor={'#00000080'}
            marginBetweenOptions={3}
            maxHeight={'80vh'}
          />
        </div>


        <div className='own-team-matrix-view-drop-down-color-bar-container'>

          <div className='own-team-matrix-view-color-bar-description own-team-matrix-view-color-bar-description-left'>
            {colorAxisLabels[0].label}
          </div>

          <div className='own-team-matrix-view-color-bar' />

          <div className='own-team-matrix-view-color-bar-description own-team-matrix-view-color-bar-description-right'>
            {colorAxisLabels[colorAxisLabels.length - 1].label}
          </div>
        </div>
      </div>


      <div className='own-team-matrix-view-matrix-section'>

        {getVerticalLinesAndLabels()}

        {getHorizontalLinesAndLabels()}

        {simulationNodes.map((player, index) => {

          const diameter = 40;

          const marginTop = -(diameter / 2) - 2;
          const marginLeft = -(diameter / 2);

          return (
            <div
              key={index + player.name}
              className='own-team-matrix-view-player'
              style={{
                left: player.left + '%',
                top: player.top + '%',
                color: showPlayerNames ? '#ffffff69' : '#ffffff00',
                height: diameter,
                width: diameter,
                marginTop: marginTop,
                marginLeft: marginLeft
              }}
              onClick={() => handlePlayerClick(player)}
            >

              <div className='own-team-matrix-view-player-image-container'>
                <PlayerImage
                  src={player.image_url}
                  size={diameter}
                  isSingleImage={true}
                  containerStyle={{
                    border: 'solid 3px ' + player.color,
                    boxSizing: 'border-box',
                    borderRadius: 100,
                    marginBottom: 4
                  }}
                  imageStyle={{
                    width: diameter - 6,
                    height: diameter - 6
                  }}
                />
              </div>

              <div
                id={'own-team-matrix-view-player-name-container-' + index}
                className='own-team-matrix-view-player-name-container'
                style={getPlayerNameContainerStyle(index, diameter)}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  className='own-team-matrix-view-player-name'
                  onClick={() => handlePlayerClick(player)}
                >
                  {getDisplayPlayerName(player.name, 10)}
                </div>
              </div>

            </div>
          );
        })}

      </div>
    </div>
  );
};
